import { ChatMessage } from '@assets/component/ChatMessage/ChatMessage';
import { ChatMessageApproveReject } from '@assets/component/ChatMessage/ChatMessageApproveReject';
import { LoadingAnimation } from '@assets/component/LoadingAnimation/LoadingAnimation';
import { ScrollPanel } from '@assets/component/Panel/ScrollPanel/ScrollPanel';
import { Flex } from '@assets/component/UI/Flex/Flex';
import {
    CHAT_REFINE_MARKER,
    FlowTaskMessage,
    getFirebase
} from '@assets/firebase/FirebaseAdapater';
import { ChatMessageData, MessageRole } from '@assets/model/RagAPI';
import { onValue, ref } from 'firebase/database';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import * as style from './TaskChatContainer.module.less';
import { useStore, useTasksStore } from '@assets/model/Store';
import { set } from 'lodash';
import {
    AgentSectionRecommendation,
    ChatMessageAgent
} from '@assets/component/ChatMessage/ChatMessageAgent';
import { ChatMessageStagingProposal } from '@assets/component/ChatMessage/ChatMessageStagingProposal';

interface TaskChatContainerProps {
    reportId: string;
    taskId: string;
    showWaitingAnimation: boolean;
    isWaitingReply?: boolean;
}

interface AgentMessage {
    type: string;
    content: AgentSectionRecommendation[];
    insertAfterIndex: number;
}

export const TaskChatContainer = observer((p: TaskChatContainerProps) => {
    //use id to fetch chat
    const { reportId, taskId, showWaitingAnimation, isWaitingReply } = p;
    const [chatContent, setChatContent] = useState(null);
    const [chatAgentMessages, setChatAgentMessages] = useState<AgentMessage[]>(
        []
    );
    const [isChatLoading, setIsChatLoading] = useState(true);
    const taskDef = useTasksStore().get_task_definition(taskId);
    useEffect(
        function fetchTaskContent() {
            //Reset state for new task
            setChatContent(null);
            setChatAgentMessages([]);
            setIsChatLoading(true);

            const path = `/flowtasks/history/${reportId}/${taskId}`;
            const fb = getFirebase();
            const r = ref(fb.db, path);
            const unsub = onValue(r, snapshot => {
                setChatContent(null);
                setChatAgentMessages(null);

                const data = snapshot.val();
                if (data && data.results) {
                    setChatContent(data.results);
                }

                if (data && data.agent_messages) {
                    setChatAgentMessages(data.agent_messages);
                }
                console.log('TaskChatPanel: fetched task chat data:', data);
                setIsChatLoading(false);
            });

            return () => {
                unsub();
            };
        },
        [taskId, reportId]
    );

    const messageData = chatContent ?? [];

    //Bit awkward to put effect after fetching data from store
    useEffect(() => {
        const chatScroll = document.getElementById('chat-scroll');
        if (chatScroll) {
            setTimeout(() => {
                chatScroll.scrollTop = chatScroll.scrollHeight;
            }, 50);
        }
    }, [messageData.length, showWaitingAnimation]);

    const handleSendAgentImprovements = (recommendation: string) => {
        var messageToSend: FlowTaskMessage = {
            content: recommendation,
            approve: false
        };
        const fb = getFirebase();
        fb.flowtask_add_prompt_job(reportId, taskId, messageToSend);
    };

    const handleApprove = (didApprove: boolean) => {
        var text = '';
        if (!didApprove) {
            text = prompt('Please provide a reason for rejection:', 'Reject');
        }
        if (text == '' || text == null) {
            text = '-';
        }

        const fb = getFirebase();
        fb.flowtask_add_prompt_job(reportId, taskId, {
            content: text,
            approve: didApprove
        });
    };
    const messages = [];

    type MessageType = {
        role: string;
        content: string;
        fileUploads?: { mime: string; name: string; type: string }[];
        action?: any;
        state?: string;
        extra_data?: any;
    };

    const allMessages: MessageType[] = [];
    for (const messageIndex in messageData) {
        const msg = messageData[messageIndex];
        // Marker-prefixed messages come from the Discuss flow
        // (CHAT_REFINE_MARKER + user text). Strip the marker so the user's
        // typed instruction renders as a normal chat bubble — this preserves
        // the prompt → response thread the user needs to navigate the chat
        // after the staging proposal is gone.
        let renderedContent = msg.content;
        if (
            typeof renderedContent === 'string' &&
            renderedContent.startsWith(CHAT_REFINE_MARKER)
        ) {
            renderedContent = renderedContent
                .slice(CHAT_REFINE_MARKER.length)
                .trim();
        }
        allMessages.push({
            role: msg.role,
            content: renderedContent,
            fileUploads: msg.fileUploads || [],
            action: msg.action || null,
            state: msg.state || null
        });

        for (const agentMsgKey in chatAgentMessages ?? []) {
            const agentMsg = chatAgentMessages[agentMsgKey];
            if (agentMsg.insertAfterIndex == parseInt(messageIndex)) {
                if (agentMsg.type == 'agentMessage') {
                    allMessages.push({
                        role: 'agentMessage',
                        content: '',
                        fileUploads: [],
                        action: null,
                        state: null,
                        extra_data: agentMsg.content
                    });
                } else {
                    console.warn('Unknown agent message type:', agentMsg.type);
                }
            }
        }
    }
    // Inject a synthetic message at the end when the comparator has produced a
    // staging proposal for this module. Mirrors the chatAgentMessages pattern
    // (MD Agent) but keyed off vars_staging/ in Firebase rather than agent
    // recommendations.
    const stagingStore = useStore();
    const stagingTaskDef = useTasksStore().get_task_definition(taskId);
    if (stagingTaskDef) {
        const staging = stagingStore.getStagingFor(
            taskId,
            stagingTaskDef.save_to
        );
        if (staging) {
            allMessages.push({
                role: 'stagingProposal',
                content: '',
                fileUploads: [],
                action: null,
                state: null,
                extra_data: staging
            });
        }
    }

    const msgHistory = allMessages;
    // const msgHistory: MessageType[] = messageData.map(msg => {
    //     return {
    //         role: msg.role,
    //         content: msg.content,
    //         fileUploads: msg.fileUploads || [],
    //         action: msg.action || null,
    //         state: msg.state || null
    //     };
    // });
    const totalMessages = msgHistory.length;
    msgHistory.forEach((msg, index) => {
        const msgId = 'msg_' + index;
        const isUserMessage = msg['role'] == 'userMessage';
        const isAgentMessage = msg['role'] == 'agentMessage';

        //const msg: any = messageData[msgId];
        if (isUserMessage) {
            const questionText: string = msg['content'];
            const msgUser: ChatMessageData = {
                role: msg['role'] as MessageRole,
                isMe: true,
                message: questionText,
                date: new Date(),
                processed: true,
                id: msgId
            };

            if (msg['fileUploads'] && msg['fileUploads'].length > 0) {
                msgUser.attachedFiles = msg['fileUploads'];
            }

            messages.push(<ChatMessage msg={msgUser} key={msgId} />);
        } else {
            const msgResponse: ChatMessageData = {
                isMe: false,
                role: msg['role'] as MessageRole,
                message: msg['content'],
                date: new Date(),
                processed: true,
                id: msgId
            };
            if (msg['role'] === 'agentMessage') {
                messages.push(
                    <ChatMessageAgent
                        msg={msgResponse}
                        improveSections={
                            (() => {
                                const d = msg.extra_data;
                                if (Array.isArray(d)) return d as AgentSectionRecommendation[];
                                if (typeof d === 'string') {
                                    try { return JSON.parse(d) as AgentSectionRecommendation[]; } catch { return []; }
                                }
                                return [];
                            })()
                        }
                        key={msgId}
                        onFinish={() => {
                            handleApprove(true);
                        }}
                        onUseRecommendation={(recommendation: string) => {
                            handleSendAgentImprovements(recommendation);
                        }}
                        isDisabled={
                            isWaitingReply || index != totalMessages - 1
                        }
                    />
                );
            } else if (msg['role'] === 'stagingProposal') {
                const stagingData = msg.extra_data as {
                    candidate: string;
                    metadata: any;
                } | null;
                if (stagingData && stagingData.candidate) {
                    messages.push(
                        <ChatMessageStagingProposal
                            key={msgId + '_staging'}
                            taskId={taskId}
                            candidate={stagingData.candidate}
                            metadata={stagingData.metadata}
                        />
                    );
                }
            } else {
                messages.push(
                    <ChatMessage msg={msgResponse} key={msgId + '_server'} />
                );
            }
        }
    });

    const lastMessage = msgHistory[msgHistory.length - 1] || null;

    if (lastMessage?.action != null) {
        messages.push(
            <ChatMessageApproveReject
                onApprove={didApprove => {
                    handleApprove(didApprove);
                }}
                isLoading={isWaitingReply}
                key={'appprove_reject_id'}
            />
        );
    }

    if (messages.length === 0) {
        const msg_starting_text: ChatMessageData = {
            isMe: false,
            message: taskDef.starting_text,
            date: new Date(),
            processed: true
        };
        messages.push(
            <ChatMessage msg={msg_starting_text} key="start_message" />
        );
    }
    if (showWaitingAnimation || isChatLoading) {
        messages.push(
            <div className="fade-in" key="animation">
                <LoadingAnimation width={200} height={100} />
            </div>
        );
    }

    return (
        <>
            <ScrollPanel
                onLoadScrollToEnd={true}
                id="chat-scroll"
                className={style.scrollFadeContent}
                key={taskId}
            >
                <Flex direction="column" justify="end">
                    {messages}
                </Flex>
            </ScrollPanel>
        </>
    );
});
